import { useEffect, useContext, useRef, useCallback } from 'react';
import { AuthContext, API } from '@/App';
import capacitorService from '@/services/CapacitorService';

const NOTIFICATION_REQUEST_KEY = 'notification_request_count';


/**
 * Hook personalizado que solicita automáticamente permisos de notificaciones
 * cuando el usuario inicia sesión y la app está instalada como PWA o App Nativa
 */
const useAutoNotificationRequest = () => {
  const { token, user } = useContext(AuthContext);
  const hasRequestedThisSessionRef = useRef(false);

  const urlBase64ToUint8Array = useCallback((base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }, []);

  // Lógica para suscripción WEB (PWA)
  const subscribeUserToPushWeb = useCallback(async () => {
    try {
      const vapidResponse = await fetch(`${API}/notifications/vapid-public-key`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!vapidResponse.ok) throw new Error('Error obteniendo VAPID key');

      const { publicKey } = await vapidResponse.json();
      const applicationServerKey = urlBase64ToUint8Array(publicKey);

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      await fetch(`${API}/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      });

      console.log('✅ Usuario suscrito a notificaciones Web');
    } catch (error) {
      console.error('Error suscribiendo a push Web:', error);
    }
  }, [token, urlBase64ToUint8Array]);

  // Lógica para suscripción NATIVA (Android/iOS)
  const subscribeUserToPushNative = useCallback(async () => {
    try {
      const fcmToken = await capacitorService.registerPushNotifications();
      if (!fcmToken) return;

      await fetch(`${API}/notifications/register-fcm-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: fcmToken })
      });

      console.log('✅ Dispositivo registrado para Push Nativo');
      
      await capacitorService.addPushListeners({
        onReceived: (notification) => console.log('Notificación recibida:', notification),
        onAction: (action) => console.log('Acción en notificación:', action)
      });
    } catch (error) {
      console.error('Error registrando push nativo:', error);
    }
  }, [token]);

  const requestNotificationPermission = useCallback(async () => {
    try {
      hasRequestedThisSessionRef.current = true;

      // Caso NATIVO
      if (capacitorService.isNativePlatform()) {
        await subscribeUserToPushNative();
        return;
      }
      
      // Caso WEB
      if (!('Notification' in window)) return;
      
      const requestCount = parseInt(localStorage.getItem(NOTIFICATION_REQUEST_KEY) || '0');
      const permissionResult = await Notification.requestPermission();

      if (permissionResult === 'granted') {
        localStorage.removeItem(NOTIFICATION_REQUEST_KEY);
        await subscribeUserToPushWeb();
      } else {
        localStorage.setItem(NOTIFICATION_REQUEST_KEY, (requestCount + 1).toString());
      }
    } catch (error) {
      console.error('Error solicitando permisos:', error);
    }
  }, [subscribeUserToPushWeb, subscribeUserToPushNative]);

  useEffect(() => {
    if (!token || !user || hasRequestedThisSessionRef.current) return;

    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true ||
                  document.referrer.includes('android-app://');

    const isNative = capacitorService.isNativePlatform();

    if (isNative || isPWA) {
      // En nativo, siempre refrescamos el token al entrar para evitar que se "trabe"
      if (isNative) {
        subscribeUserToPushNative();
      } else {
        const timer = setTimeout(() => {
          requestNotificationPermission();
        }, 3000);
        return () => clearTimeout(timer);
      }
    }
  }, [token, user, requestNotificationPermission]);
};

export default useAutoNotificationRequest;
